<template>
  <!--
    手機版 App 模式的底部導覽列：登入後才出現，只在 utils/appTabBar.js 列的那幾頁。
    「商品訂單」「我的訂閱」直接進會員中心（views/Member/Center.vue）的那個分頁，
    其餘分頁收在「更多」，點了從底部滑出清單（刪除帳號的入口在「帳號安全管理」裡面）。
    「邀請分享」不是一頁，點了打開分享視窗，分享的是「目前這一頁」（見 SHARE_PAGES）。
  -->
  <div v-if="visible">
    <!-- 佔位：頁面最底下留出導覽列的高度，頁尾才不會被蓋住 -->
    <div class="app-tabbar-spacer" aria-hidden="true"></div>

    <nav class="app-tabbar" aria-label="主選單">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        class="app-tabbar__item"
        :class="{ 'app-tabbar__item--active primary--text': isActive(t) }"
        :aria-current="isActive(t) && t.to ? 'page' : null"
        :aria-expanded="t.action === 'more' ? String(showMore) : null"
        :aria-haspopup="t.action === 'more' ? 'true' : null"
        @click="onTap(t)"
      >
        <v-icon size="24" :color="isActive(t) ? 'primary' : 'grey darken-1'">{{ t.icon }}</v-icon>
        <span>{{ t.label }}</span>
      </button>
    </nav>

    <ShareDialog v-model="showShare" :url="share.url" :title="share.title" :prefix-text="share.prefixText" :qr-text="share.qrText" />

    <!-- 「更多」：從底部滑出清單。Vuetify 2 的 bottom sheet 寬度跟內容走，要給 100% 才會滿版 -->
    <v-bottom-sheet v-model="showMore" width="100%">
      <v-sheet class="app-more-sheet">
        <div class="app-more-sheet__handle"></div>
        <div class="d-flex align-center px-4 pb-1">
          <div class="text-subtitle-1 font-weight-bold">更多</div>
          <v-spacer></v-spacer>
          <v-btn icon aria-label="關閉" @click="showMore = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-list class="pt-0 pb-2">
          <v-list-item
            v-for="item in moreItems"
            :key="item.key"
            :class="{ 'primary--text app-more-sheet__item--active': isCurrent(item) }"
            @click="go(item.to)"
          >
            <v-list-item-icon class="mr-4">
              <v-icon :color="isCurrent(item) ? 'primary' : 'grey darken-1'">{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-body-1 font-weight-medium">{{ item.label }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="my-0">
              <v-icon v-if="isCurrent(item)" color="primary">mdi-check</v-icon>
              <v-icon v-else color="grey lighten-1">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-sheet>
    </v-bottom-sheet>
  </div>
</template>

<script>
import ShareDialog from "./ShareDialog.vue";
import { showsAppTabBar } from "@/utils/appTabBar";
import { Grade_Overview } from "@/apis/api";

/*
  「邀請分享」依目前在哪一頁分享不同的連結，網址都帶自己的推薦碼（refc）。
  refc 是進站那一刻從網址記下來的（router 的 rememberReferralFromUrl），不限哪一頁，
  所以從商品中心分享出去，對方從商品中心進站一樣掛得上推薦人。

  沒列在這裡的（首頁、會員中心）照舊分享首頁的會員邀請 ——
  會員中心要登入才看得到，分享出去對方也進不去。
  keepQuery：要一起帶走的網址參數（商品中心目前選的分類）。
*/
const SHARE_PAGES = {
  Product: {
    title: "商品中心",
    prefixText: "邀請您逛逛小芋頭商品中心",
    qrText: "小芋頭商品中心",
    keepQuery: ["category"],
  },
  Upgrade: {
    title: "終身俸計畫",
    prefixText: "邀請您了解小芋頭終身俸計畫",
    qrText: "小芋頭終身俸計畫",
    keepQuery: [],
  },
};

const centerTab = (tab) => ({ name: "Center", query: { tab } });

export default {
  name: "AppTabBar",
  components: { ShareDialog },
  data() {
    return {
      showShare: false,
      showMore: false,
      // 有代理商資格（合約生效中）才放「小芋頭代理商」，跟會員中心分頁的判斷一樣
      hasGrade: false,
      gradeCheckedFor: null,
      // icon 跟會員中心各分頁上方的大標題一致（Center.vue 的 items），改的話一起改
      tabs: [
        { key: "home", label: "首頁", icon: "mdi-home", to: { name: "Home" } },
        { key: "order", label: "商品訂單", icon: "mdi-invoice-text", to: centerTab("order") },
        { key: "share", label: "邀請分享", icon: "mdi-share-variant", action: "share" },
        { key: "subscription", label: "我的訂閱", icon: "mdi-briefcase", to: centerTab("subscription") },
        { key: "more", label: "更多", icon: "mdi-menu", action: "more" },
      ],
    };
  },
  computed: {
    visible() {
      return this.$vuetify.breakpoint.xsOnly && showsAppTabBar(this.$route, this.$store.state.signinStatus);
    },
    moreItems() {
      return [
        { key: "chicken", label: "雞肉飼料", icon: "mdi-currency-usd", to: centerTab("chicken") },
        this.hasGrade && { key: "agent", label: "小芋頭代理商", icon: "mdi-account-group", to: centerTab("agent") },
        { key: "profile", label: "修改個人資料", icon: "mdi-account", to: centerTab("profile") },
        // 刪除帳號的入口在這個分頁裡（TabSecurity.vue），不另外放一項
        { key: "security", label: "帳號安全管理", icon: "mdi-shield-account", to: centerTab("security") },
      ].filter(Boolean);
    },
    /** 會員中心目前在哪個分頁；網址沒帶 tab 時會員中心顯示第一個（個人資料） */
    centerTabKey() {
      if (this.$route.name !== "Center") return null;
      return this.$route.query.tab || "profile";
    },
    /** 分享視窗的內容；null 的欄位交給 ShareDialog 用預設（首頁的會員邀請） */
    share() {
      const page = SHARE_PAGES[this.$route.name];
      if (!page) return { url: null, title: null, prefixText: null, qrText: null };

      const emailCode = this.$store.state.username || localStorage.getItem("_emailEncrypt") || "";
      const url = new URL(this.$route.path, process.env.VUE_APP_MAIN_BASE_URL);
      for (const key of page.keepQuery) {
        const v = this.$route.query[key];
        if (v !== undefined && v !== null && v !== "") url.searchParams.set(key, Array.isArray(v) ? v[0] : v);
      }
      url.searchParams.set("refc", emailCode);

      return { url: url.toString(), title: page.title, prefixText: page.prefixText, qrText: page.qrText };
    },
  },
  watch: {
    // 換頁（含會員中心換分頁）時分享視窗、更多面板跟著關掉
    $route() {
      this.showShare = false;
      this.showMore = false;
    },
    visible: {
      immediate: true,
      handler(v) {
        if (v) this.checkGrade();
      },
    },
    // 面板開著時藏起小芋頭助手（它的 z-index 比面板高）
    showMore(open) {
      document.body.classList.toggle("taro-assistant-hidden", open);
    },
  },
  beforeDestroy() {
    if (this.showMore) document.body.classList.remove("taro-assistant-hidden");
  },
  methods: {
    isActive(t) {
      // 分享視窗或更多面板開著時只亮那一格
      if (t.action === "share") return this.showShare;
      if (t.action === "more") return this.showMore || (!this.showShare && this.moreItems.some((i) => this.isCurrent(i)));
      if (this.showShare || this.showMore) return false;
      if (t.to.name === "Center") return this.centerTabKey === t.to.query.tab;
      return this.$route.name === t.to.name;
    },
    isCurrent(item) {
      return this.centerTabKey === item.to.query.tab;
    },
    onTap(t) {
      if (t.action === "share") {
        this.showMore = false;
        this.showShare = true;
        return;
      }
      if (t.action === "more") {
        this.showMore = true;
        return;
      }
      this.go(t.to);
    },
    go(to) {
      this.showMore = false;
      // 已經在這一頁：跟 App 一樣捲回最上面
      if (to.name === "Center" ? this.centerTabKey === to.query.tab : this.$route.name === to.name) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      this.$router.push(to).catch(() => {});
    },
    /** 每個帳號查一次就好；換帳號登入會再查 */
    async checkGrade() {
      const accountGuid = localStorage.getItem("_signinUserGuid");
      if (!accountGuid || this.gradeCheckedFor === accountGuid) return;
      this.gradeCheckedFor = accountGuid;
      try {
        const res = await Grade_Overview(accountGuid);
        const status = res.data && res.data.status;
        this.hasGrade = status === 2 || status === 3;
      } catch (err) {
        this.hasGrade = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.app-tabbar-spacer {
  height: calc(56px + env(safe-area-inset-bottom));
}

.app-tabbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  /* 頁面的固定 header 是 100；對話框、底部面板（200 以上）要蓋得過它 */
  z-index: 101;
  display: flex;
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  /* iPhone 底部的橫條區域 */
  padding-bottom: env(safe-area-inset-bottom);
}

.app-tabbar__item {
  flex: 1 1 0;
  min-width: 0;
  height: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: 0;
  background: none;
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  span {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: -4px;
  }
}

.app-tabbar__item--active {
  font-weight: 700;
}

/* 「更多」面板 */
.app-more-sheet {
  border-radius: 16px 16px 0 0 !important;
  padding-bottom: env(safe-area-inset-bottom);
}

.app-more-sheet__handle {
  width: 36px;
  height: 4px;
  margin: 8px auto 4px;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.2);
}

.app-more-sheet__item--active {
  background: rgba(124, 77, 255, 0.08);
}
</style>
